import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckIcon from "@material-ui/icons/Check";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import WarningOutlinedIcon from "@material-ui/icons/WarningOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { i18n } from "../../translate/i18n";
import {
  scoreColor,
  scoreBadgeLabel,
  temperatureColor,
  outcomeColor,
  verdictColor,
  verdictLabel,
  eventKindColor,
  emotionColor,
  emotionLabel,
  impactColor,
  impactLabel,
  levelLabel,
  vsTeamLabel,
  stageLabel,
  temperatureLabel,
  outcomeLabel,
  formatBRL,
  formatPercent,
} from "../../utils/saleAudit";

const drawerWidth = 480;

const useStyles = makeStyles((theme) => ({
  drawer: {
    // Só reserva largura quando ABERTO. Um drawer `persistent` fechado
    // continua ocupando a coluna flex, e com dois deles (contato 320 +
    // auditoria 480) sobravam 271px para a conversa num monitor de 1366 — as
    // mensagens quebravam uma palavra por linha. O `down("sm")` abaixo não
    // cobria isso: o problema aparece em desktop, não só no celular.
    width: ({ open }) => (open ? drawerWidth : 0),
    flexShrink: 0,
    // Idem ContactDrawer: como overlay, não reserva largura na linha flex. Aqui
    // era pior — 480px de reserva num viewport de 375px.
    [theme.breakpoints.down("sm")]: {
      width: "auto",
    },
  },
  drawerPaper: {
    width: drawerWidth,
    maxWidth: "100%",
    display: "flex",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  header: {
    display: "flex",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: "#eee",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    minHeight: "73px",
    justifyContent: "flex-start",
  },
  content: {
    display: "flex",
    backgroundColor: "#eee",
    flexDirection: "column",
    padding: 8,
    height: "100%",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    gap: 8,
  },
  section: {
    padding: 8,
  },
  sectionTitle: {
    fontWeight: 700,
  },
  // (a) hero
  scoreBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 12,
  },
  scoreValue: {
    fontSize: 52,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    justifyContent: "center",
    marginTop: 8,
  },
  metaGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "2px 14px",
    marginTop: 10,
  },
  metaItem: {
    fontSize: 12,
    color: "rgba(0,0,0,0.55)",
  },
  // (b) jogada
  actionBox: {
    padding: 8,
    borderLeft: "3px solid #2563EB",
  },
  messageBox: {
    padding: 8,
    background: "#fff",
    border: "1px dashed rgba(0,0,0,0.2)",
    borderRadius: 4,
    whiteSpace: "pre-wrap",
    fontSize: 13,
    marginTop: 4,
  },
  // (c) categorias
  categoryRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "3px 0",
  },
  categoryLabel: {
    width: 130,
    flexShrink: 0,
    fontSize: 13,
  },
  categoryTrack: {
    flex: 1,
    background: "rgba(0,0,0,0.08)",
    borderRadius: 4,
    height: 10,
    overflow: "hidden",
  },
  categoryBar: {
    height: "100%",
    borderRadius: 4,
    minWidth: 2,
    transition: "width .3s",
  },
  categoryScore: {
    width: 30,
    textAlign: "right",
    fontSize: 13,
    fontWeight: 700,
  },
  evidenceBox: {
    margin: "2px 0 6px 8px",
    paddingLeft: 8,
    borderLeft: "2px solid rgba(0,0,0,0.15)",
  },
  // (c2) benchmark
  benchmarkHeadline: {
    fontWeight: 700,
  },
  benchmarkRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
    padding: "3px 0",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  benchmarkDelta: {
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  // (d) timeline
  bubbleWrapper: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 10,
  },
  bubble: {
    maxWidth: "88%",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 13,
    whiteSpace: "pre-wrap",
  },
  bubbleClient: {
    alignSelf: "flex-start",
    background: "#E9EDF1",
    borderTopLeftRadius: 0,
  },
  bubbleSeller: {
    alignSelf: "flex-end",
    background: "#DCF8C6",
    borderTopRightRadius: 0,
  },
  bubbleNote: {
    maxWidth: "88%",
    fontSize: 12,
    marginTop: 2,
    fontWeight: 600,
  },
  bubbleMeta: {
    fontSize: 11,
    color: "rgba(0,0,0,0.45)",
  },
  // (e) eventos
  eventsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  // (f) emoções
  emotionTrack: {
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
    overflowX: "auto",
    padding: "8px 2px 2px",
    ...theme.scrollbarStyles,
  },
  emotionItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    minWidth: 62,
    flexShrink: 0,
  },
  emotionDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
  },
  emotionLine: {
    flex: 1,
    minWidth: 12,
    height: 2,
    background: "rgba(0,0,0,0.15)",
    marginTop: 5,
  },
  // (g) listas
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    padding: "3px 0",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 12,
    padding: 24,
  },
}));

const solidChip = (label, color) => (
  <Chip
    size="small"
    label={label}
    style={{ backgroundColor: color, color: "#fff", fontWeight: 600 }}
  />
);

const outlinedChip = (label, color) => (
  <Chip
    size="small"
    variant="outlined"
    label={label}
    style={{ borderColor: color, color, fontWeight: 600 }}
  />
);

// "google/gemini-3-flash-preview" → "gemini-3-flash-preview"
const shortModel = (model) => {
  if (!model) return null;
  const parts = String(model).split("/");
  return parts[parts.length - 1];
};

const formatAnalysisSeconds = (analysisMs) => {
  const n = Number(analysisMs);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${(n / 1000).toFixed(1)}s`;
};

const vsTeamColor = (vsTeam) => {
  if (vsTeam === "acima") return "#0F766E";
  if (vsTeam === "abaixo") return "#E5484D";
  return "#6B7280";
};

const SaleAuditDrawer = ({ open, handleDrawerClose, ticket }) => {
  const classes = useStyles({ open });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const ticketId = ticket?.id;

  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    setAudit(null);
    setExpandedCategories({});
    if (!ticketId || !open) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/sale-audits/ticket/${ticketId}`);
        if (active) setAudit(data);
      } catch (err) {
        // 404 = ainda sem auditoria → estado vazio, sem toast.
        if (err?.response?.status !== 404) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [ticketId, open]);

  useEffect(() => {
    if (!ticketId) return;
    const socket = openSocket();
    socket.on("saleAudit", (data) => {
      if (
        data.action === "update" &&
        Number(data.audit?.ticketId) === Number(ticketId)
      ) {
        setAudit(data.audit);
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  const handleAnalyze = useCallback(async () => {
    if (!ticketId) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(
        `/sale-audits/ticket/${ticketId}/analyze`,
      );
      setAudit(data);
    } catch (err) {
      toastError(err);
    } finally {
      setAnalyzing(false);
    }
  }, [ticketId]);

  const handleCopy = async () => {
    if (!audit?.suggestedMessage) return;
    try {
      await navigator.clipboard.writeText(audit.suggestedMessage);
      toast.success(i18n.t("saleAuditDrawer.toasts.copied"));
    } catch (err) {
      toast.error(i18n.t("saleAuditDrawer.toasts.copyError"));
    }
  };

  const toggleCategory = (key) =>
    setExpandedCategories((prev) => ({ ...prev, [key]: !prev[key] }));

  // Audits v1 antigos: arrays ausentes → seções escondidas.
  const categories = Array.isArray(audit?.categories) ? audit.categories : [];
  const messageComments = Array.isArray(audit?.messageComments)
    ? audit.messageComments
    : [];
  const events = Array.isArray(audit?.events) ? audit.events : [];
  const emotions = Array.isArray(audit?.emotions) ? audit.emotions : [];
  const positives = Array.isArray(audit?.positives) ? audit.positives : [];
  const issues = Array.isArray(audit?.issues) ? audit.issues : [];
  const missedOpportunities = Array.isArray(audit?.missedOpportunities)
    ? audit.missedOpportunities
    : [];
  const coaching = audit?.coaching || null;
  // Amostra pequena → backend manda null e o bloco inteiro some.
  const benchmark = audit?.benchmark || null;
  const benchmarkCategories = Array.isArray(benchmark?.categories)
    ? benchmark.categories
    : [];

  const analysisSeconds = formatAnalysisSeconds(audit?.analysisMs);
  const model = shortModel(audit?.model);

  return (
    <Drawer
      className={classes.drawer}
      variant={isMobile ? "temporary" : "persistent"}
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
      ModalProps={{
        container: document.getElementById("drawer-container"),
        style: { position: "absolute" },
      }}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.header}>
        <IconButton onClick={handleDrawerClose}>
          <CloseIcon />
        </IconButton>
        <Typography style={{ justifySelf: "center" }}>
          {i18n.t("saleAuditDrawer.header")}
        </Typography>
        {ticketId && (
          <Button
            size="small"
            color="primary"
            style={{ marginLeft: "auto" }}
            onClick={() => history.push(`/sale-audits/${ticketId}`)}
          >
            {i18n.t("saleAuditDetail.buttons.openFull")}
          </Button>
        )}
      </div>

      <div className={classes.content}>
        {loading && (
          <div className={classes.emptyState}>
            <CircularProgress size={28} />
          </div>
        )}

        {!loading && !audit && (
          <div className={classes.emptyState}>
            <AssessmentOutlinedIcon
              style={{ fontSize: 48, color: "#9CA3AF" }}
            />
            <Typography variant="body1">
              {i18n.t("saleAuditDrawer.empty.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("saleAuditDrawer.empty.description")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={analyzing}
              startIcon={
                analyzing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={handleAnalyze}
            >
              {i18n.t("saleAuditDrawer.buttons.analyze")}
            </Button>
          </div>
        )}

        {!loading && audit && (
          <>
            {/* (a) score hero + badge + chips de venda + meta */}
            <Paper square variant="outlined" className={classes.scoreBox}>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("saleAuditDrawer.score")}
              </Typography>
              <span
                className={classes.scoreValue}
                style={{ color: scoreColor(audit.score) }}
              >
                {audit.score}
              </span>
              {solidChip(scoreBadgeLabel(audit.score), scoreColor(audit.score))}
              <div className={classes.chipsRow}>
                {solidChip(stageLabel(audit.stage), "#6B7280")}
                {solidChip(
                  temperatureLabel(audit.temperature),
                  temperatureColor(audit.temperature),
                )}
                {solidChip(
                  outcomeLabel(audit.outcome),
                  outcomeColor(audit.outcome),
                )}
                {outlinedChip(
                  `${i18n.t("saleAuditDrawer.probability")}: ${formatPercent(
                    audit.probability,
                  )}`,
                  "#2563EB",
                )}
                {audit.potentialValue !== null &&
                  audit.potentialValue !== undefined &&
                  outlinedChip(formatBRL(audit.potentialValue), "#0F766E")}
              </div>
              <div className={classes.metaGrid}>
                <span className={classes.metaItem}>
                  {i18n.t("saleAuditDrawer.confidence")}:{" "}
                  {formatPercent(audit.confidence)}
                </span>
                {model && (
                  <span className={classes.metaItem}>
                    {i18n.t("saleAuditDrawer.model")}: {model}
                  </span>
                )}
                {analysisSeconds && (
                  <span className={classes.metaItem}>
                    {i18n.t("saleAuditDrawer.analysisTime")}: {analysisSeconds}
                  </span>
                )}
                {audit.analyzedAt && (
                  <span className={classes.metaItem}>
                    {i18n.t("saleAuditDrawer.analyzedAt")}:{" "}
                    {new Date(audit.analyzedAt).toLocaleString("pt-BR")}
                  </span>
                )}
              </div>
            </Paper>

            {/* (b) jogada sugerida */}
            {(audit.suggestedAction || audit.suggestedMessage) && (
              <Paper square variant="outlined" className={classes.actionBox}>
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("saleAuditDrawer.sections.play")}
                </Typography>
                {audit.suggestedAction && (
                  <Typography variant="body2" style={{ fontWeight: 700 }}>
                    {audit.suggestedAction}
                  </Typography>
                )}
                {audit.suggestedMessage && (
                  <>
                    <div className={classes.messageBox}>
                      {audit.suggestedMessage}
                    </div>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<FileCopyOutlinedIcon />}
                      onClick={handleCopy}
                      style={{ marginTop: 6 }}
                    >
                      {i18n.t("saleAuditDrawer.buttons.copy")}
                    </Button>
                  </>
                )}
              </Paper>
            )}

            {/* (c) breakdown das 15 categorias com evidências */}
            {categories.length > 0 && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.categories")}
                </Typography>
                {categories.map((cat) => {
                  const hasEvidence =
                    Array.isArray(cat.evidence) && cat.evidence.length > 0;
                  const expanded = !!expandedCategories[cat.key];
                  return (
                    <div key={cat.key}>
                      <div className={classes.categoryRow}>
                        <span className={classes.categoryLabel}>
                          {cat.label}
                        </span>
                        <div className={classes.categoryTrack}>
                          <div
                            className={classes.categoryBar}
                            style={{
                              width: `${Math.max(
                                0,
                                Math.min(100, Number(cat.score) || 0),
                              )}%`,
                              background: scoreColor(cat.score),
                            }}
                          />
                        </div>
                        <span
                          className={classes.categoryScore}
                          style={{ color: scoreColor(cat.score) }}
                        >
                          {Math.round(Number(cat.score) || 0)}
                        </span>
                        {hasEvidence ? (
                          <Tooltip
                            title={i18n.t(
                              expanded
                                ? "saleAuditDrawer.evidence.hide"
                                : "saleAuditDrawer.evidence.show",
                            )}
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleCategory(cat.key)}
                            >
                              {expanded ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <span style={{ width: 30 }} />
                        )}
                      </div>
                      {hasEvidence && (
                        <Collapse in={expanded}>
                          <div className={classes.evidenceBox}>
                            {cat.evidence.map((ev, i) => (
                              <div key={i} style={{ padding: "2px 0" }}>
                                <Typography
                                  variant="body2"
                                  style={{ fontStyle: "italic", fontSize: 13 }}
                                >
                                  “{ev.excerpt}”
                                </Typography>
                                {ev.idx !== null && ev.idx !== undefined && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {i18n.t("saleAuditDrawer.evidence.msgRef")}
                                    {ev.idx}
                                  </Typography>
                                )}
                              </div>
                            ))}
                          </div>
                        </Collapse>
                      )}
                    </div>
                  );
                })}
              </Paper>
            )}

            {/* (c2) benchmark vs. equipe */}
            {benchmark && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.benchmark")}
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.benchmarkHeadline}
                  style={{ color: scoreColor(benchmark.betterThanPct) }}
                >
                  {i18n.t("saleAuditDrawer.benchmark.betterThan", {
                    pct: Math.round(Number(benchmark.betterThanPct) || 0),
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.benchmark.sampleSize", {
                    count: Math.round(Number(benchmark.sampleSize) || 0),
                  })}
                </Typography>
                {benchmarkCategories.map((cat) => {
                  const delta = Math.round(Number(cat.delta) || 0);
                  const color =
                    delta > 0 ? "#0F766E" : delta < 0 ? "#E5484D" : "#6B7280";
                  return (
                    <div key={cat.key} className={classes.benchmarkRow}>
                      <div>
                        <Typography variant="body2">{cat.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {i18n.t("saleAuditDrawer.benchmark.vsTeamAvg", {
                            score: Math.round(Number(cat.score) || 0),
                            teamAvg: Math.round(Number(cat.teamAvg) || 0),
                          })}
                        </Typography>
                      </div>
                      <span
                        className={classes.benchmarkDelta}
                        style={{ color }}
                      >
                        {delta > 0 ? "+" : delta < 0 ? "−" : ""}
                        {Math.abs(delta)}
                      </span>
                    </div>
                  );
                })}
              </Paper>
            )}

            {/* (d) timeline comentada */}
            {messageComments.length > 0 && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.timeline")}
                </Typography>
                {messageComments.map((comment, i) => {
                  const isSeller = comment.author === "vendedor";
                  const align = isSeller ? "flex-end" : "flex-start";
                  return (
                    <div key={i} className={classes.bubbleWrapper}>
                      <span
                        className={classes.bubbleMeta}
                        style={{ alignSelf: align }}
                      >
                        {isSeller
                          ? i18n.t("saleAuditDrawer.timeline.seller")
                          : i18n.t("saleAuditDrawer.timeline.client")}
                        {comment.idx !== null && comment.idx !== undefined
                          ? ` · ${i18n.t(
                              "saleAuditDrawer.evidence.msgRef",
                            )}${comment.idx}`
                          : ""}
                      </span>
                      <div
                        className={`${classes.bubble} ${
                          isSeller ? classes.bubbleSeller : classes.bubbleClient
                        }`}
                      >
                        {comment.excerpt}
                      </div>
                      {comment.note && (
                        <Typography
                          className={classes.bubbleNote}
                          style={{
                            alignSelf: align,
                            color: verdictColor(comment.verdict),
                          }}
                        >
                          {verdictLabel(comment.verdict)} — {comment.note}
                        </Typography>
                      )}
                    </div>
                  );
                })}
              </Paper>
            )}

            {/* (e) eventos importantes */}
            {events.length > 0 && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.events")}
                </Typography>
                <div className={classes.eventsRow}>
                  {events.map((event, i) => (
                    <Chip
                      key={i}
                      size="small"
                      variant="outlined"
                      label={`${event.at} ${event.label}`}
                      style={{
                        borderColor: eventKindColor(event.kind),
                        color: eventKindColor(event.kind),
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </div>
              </Paper>
            )}

            {/* (f) evolução emocional */}
            {emotions.length > 0 && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.emotions")}
                </Typography>
                <div className={classes.emotionTrack}>
                  {emotions.map((point, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <div className={classes.emotionLine} />}
                      <div className={classes.emotionItem}>
                        <div
                          className={classes.emotionDot}
                          style={{
                            background: emotionColor(point.emotion),
                          }}
                        />
                        <Typography
                          variant="caption"
                          style={{
                            color: emotionColor(point.emotion),
                            fontWeight: 600,
                            lineHeight: 1.1,
                          }}
                        >
                          {emotionLabel(point.emotion)}
                        </Typography>
                        {point.idx !== null && point.idx !== undefined && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ lineHeight: 1 }}
                          >
                            {i18n.t("saleAuditDrawer.evidence.msgRef")}
                            {point.idx}
                          </Typography>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </Paper>
            )}

            {/* (g) positivos / problemas / oportunidades perdidas */}
            {positives.length > 0 && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.positives")}
                </Typography>
                {positives.map((item, i) => (
                  <div key={i} className={classes.listItem}>
                    <CheckIcon style={{ color: "#0F766E", fontSize: 18 }} />
                    <Typography variant="body2">{item}</Typography>
                  </div>
                ))}
              </Paper>
            )}

            {issues.length > 0 && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.issues")}
                </Typography>
                {issues.map((issue, i) => (
                  <div key={i} className={classes.listItem}>
                    {outlinedChip(
                      impactLabel(issue.impact),
                      impactColor(issue.impact),
                    )}
                    <Typography variant="body2">{issue.label}</Typography>
                  </div>
                ))}
              </Paper>
            )}

            {missedOpportunities.length > 0 && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.missed")}
                </Typography>
                {missedOpportunities.map((item, i) => (
                  <div key={i} className={classes.listItem}>
                    <WarningOutlinedIcon
                      style={{ color: "#EA580C", fontSize: 18 }}
                    />
                    <Typography variant="body2">{item}</Typography>
                  </div>
                ))}
              </Paper>
            )}

            {/* (h) resumo executivo */}
            {audit.summary && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.summary")}
                </Typography>
                <Typography variant="body2">{audit.summary}</Typography>
              </Paper>
            )}

            {/* (i) coaching */}
            {coaching && (
              <Paper square variant="outlined" className={classes.section}>
                <Typography
                  variant="subtitle2"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  {i18n.t("saleAuditDrawer.sections.coaching")}
                </Typography>
                <div className={classes.eventsRow} style={{ marginBottom: 6 }}>
                  {(Array.isArray(coaching.train) ? coaching.train : []).map(
                    (topic, i) => (
                      <Chip
                        key={i}
                        size="small"
                        variant="outlined"
                        label={`${i18n.t(
                          "saleAuditDrawer.coaching.train",
                        )}: ${topic}`}
                        style={{
                          borderColor: "#2563EB",
                          color: "#2563EB",
                          fontWeight: 600,
                        }}
                      />
                    ),
                  )}
                </div>
                <div className={classes.eventsRow}>
                  {coaching.level &&
                    outlinedChip(
                      `${i18n.t("saleAuditDrawer.coaching.level")}: ${levelLabel(
                        coaching.level,
                      )}`,
                      "#6B7280",
                    )}
                  {coaching.vsTeam &&
                    outlinedChip(
                      `${i18n.t(
                        "saleAuditDrawer.coaching.vsTeam",
                      )}: ${vsTeamLabel(coaching.vsTeam)}`,
                      vsTeamColor(coaching.vsTeam),
                    )}
                  {coaching.improveProb !== null &&
                    coaching.improveProb !== undefined &&
                    outlinedChip(
                      `${i18n.t(
                        "saleAuditDrawer.coaching.improveProb",
                      )}: ${formatPercent(coaching.improveProb)}`,
                      "#0F766E",
                    )}
                </div>
              </Paper>
            )}

            {/* (j) reanalisar */}
            <Button
              variant="outlined"
              color="primary"
              disabled={analyzing}
              startIcon={
                analyzing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AssessmentOutlinedIcon />
                )
              }
              onClick={handleAnalyze}
              style={{ margin: "4px 0 12px" }}
            >
              {i18n.t("saleAuditDrawer.buttons.reanalyze")}
            </Button>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default SaleAuditDrawer;
